import { gql } from "@apollo/client";
import FormSection from "controls/FormSection";
import MultiValuesField from "controls/MultiValuesField";
import { UNRESTRICTED_SPORTS } from "helpers/constants";
import fetchData from "helpers/fetchData";
import sameOrNull from "helpers/sameOrNull";
import toCapitalizedWords from "helpers/toCapitalizedWords";
import useData from "hooks/useData";
import useIsAdmin from "hooks/useIsAdmin";
import NumberInput from "inputs/NumberInput";
import SelectInput from "inputs/SelectInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import React, { useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

export const LeaguesFormFragment = gql`
  fragment LeaguesFormFragment on League {
    id
    org {
      id
      allowEncodeHighQualityOutputs
    }
    competition {
      id
    }
    sport
    name
    age
    gender
    active
    potwEnabled
    aiStatsEnabled
    encodeHighQualityOutputs
    videoRecordingPermitted
    sharingDisabled
    anonymisePeople
    pointsForWin
    pointsForDraw
    pointsForLoss
    scoreboardFoulsVisible
    leaderboardTeamStandingVisible
    adPreset {
      id
    }
    external
  }
`;

export default function LeaguesForm({ leagues }) {
  const [data] = useData(gql`
    query LeaguesForm {
      sports
    }
  `);

  leagues = leagues?.map((league) => ({
    active: true,
    videoRecordingPermitted: true,
    pointsForWin: 2,
    pointsForDraw: 1,
    pointsForLoss: 0,
    ...league,
  }));

  const isAdmin = useIsAdmin();
  const [sports, sportsSet] = useState(leagues?.map((l) => l.sport));
  const [adPresets, adPresetsSet] = useState(leagues?.map((l) => l.adPreset));
  const [potwEnableds, potwEnabledsSet] = useState(leagues?.map((l) => l.potwEnabled));
  const [aiStatsEnableds, aiStatsEnabledsSet] = useState(leagues?.map((l) => l.aiStatsEnabled));
  const [videoRecordingPermitteds, videoRecordingPermittedsSet] = useState(
    leagues?.map((l) => l.videoRecordingPermitted),
  );
  const [sharingDisableds, sharingDisabledsSet] = useState(leagues?.map((l) => l.sharingDisabled));
  const [anonymisePeoples, anonymisePeoplesSet] = useState(leagues?.map((l) => l.anonymisePeople));
  const [pointsForWins, pointsForWinsSet] = useState(leagues?.map((l) => l.pointsForWin));
  const [pointsForDraws, pointsForDrawsSet] = useState(leagues?.map((l) => l.pointsForDraw));
  const [pointsForLosses, pointsForLossesSet] = useState(leagues?.map((l) => l.pointsForLoss));
  const [scoreboardFoulsVisibles, scoreboardFoulsVisiblesSet] = useState(leagues?.map((l) => l.scoreboardFoulsVisible));
  const [leaderboardTeamStandingVisibles, leaderboardTeamStandingVisiblesSet] = useState(
    leagues?.map((l) => l.leaderboardTeamStandingVisible),
  );
  const [encodeHighQualityOutputs, encodeHighQualityOutputsSet] = useState(
    leagues?.map((l) => l.encodeHighQualityOutputs),
  );

  return (
    <>
      <MultiValuesField
        label="Organisation"
        name="org"
        required
        disabled={leagues?.some((league) => league.id)}
        defaultValues={leagues?.map((league) => league.org)}
        input={<ReadminObjectInput optionsTypename="Org" />}
        helperTextUiStringKey="readmin.league.fields.org"
      />
      <MultiValuesField
        label="Competition"
        name="competition"
        disabled={leagues?.some((l) => l.external)}
        defaultValues={leagues?.map((l) => l.competition)}
        onChange={async (competitionsNew) => {
          const competitionNew = sameOrNull(competitionsNew);
          if (!competitionNew) return;
          const result = await fetchData(
            gql`
              query LeaguesForm($competitionId: ID!) {
                competition(id: $competitionId) {
                  id
                  sport
                  potwEnabled
                  aiStatsEnabled
                  adPreset {
                    id
                  }
                  videoRecordingPermitted
                  encodeHighQualityOutputs
                  sharingDisabled
                  anonymisePeople
                  pointsForWin
                  pointsForDraw
                  pointsForLoss
                  scoreboardFoulsVisible
                  leaderboardTeamStandingVisible
                }
              }
            `,
            { competitionId: competitionNew.id },
          );
          sportsSet([result.competition.sport]);
          adPresetsSet([result.competition.adPreset]);
          // TODO: remove aged-based org default then make this non-nullable
          if (result.competition.potwEnabled !== null) potwEnabledsSet([result.competition.potwEnabled]);
          if (result.competition.aiStatsEnabled !== null) aiStatsEnabledsSet([result.competition.aiStatsEnabled]);
          if (result.competition.videoRecordingPermitted !== null)
            videoRecordingPermittedsSet([result.competition.videoRecordingPermitted]);
          sharingDisabledsSet([result.competition.sharingDisabled]);
          anonymisePeoplesSet([result.competition.anonymisePeople]);
          pointsForWinsSet([result.competition.pointsForWin]);
          pointsForDrawsSet([result.competition.pointsForDraw]);
          pointsForLossesSet([result.competition.pointsForLoss]);
          scoreboardFoulsVisiblesSet([result.competition.scoreboardFoulsVisible]);
          leaderboardTeamStandingVisiblesSet([result.competition.leaderboardTeamStandingVisible]);
          encodeHighQualityOutputsSet([result.competition.encodeHighQualityOutputs]);
        }}
        input={<ReadminObjectInput optionsTypename="Competition" />}
        helperTextUiStringKey="readmin.league.fields.competition"
      />
      <MultiValuesField
        label="Sport"
        name="sport"
        required
        values={sports}
        onChange={sportsSet}
        disabled={
          !isAdmin &&
          (leagues?.filter((l) => l.id).some((l) => l.sport && !UNRESTRICTED_SPORTS.includes(l.sport)) ?? false)
        }
        input={
          <SelectInput
            options={[
              ...(data?.sports ?? []),
              ...(leagues
                ?.filter((l) => l.id)
                .map((l) => l.sport)
                .filter((s) => s && !data?.sports?.includes(s)) ?? []),
            ]
              .filter(
                (sport) =>
                  isAdmin ||
                  UNRESTRICTED_SPORTS.includes(sport) ||
                  leagues?.filter((l) => l.id).some((l) => l.sport === sport),
              )
              .map((sport) => [toCapitalizedWords(sport), sport])}
          />
        }
        helperTextUiStringKey="readmin.league.fields.sport"
      />
      <MultiValuesField
        label="Name"
        name="name"
        required
        disabled={leagues?.some((league) => league.external)}
        defaultValues={leagues?.map((league) => league.name)}
        input={<TextInput />}
        helperTextUiStringKey="readmin.league.fields.name"
      />
      <MultiValuesField
        label="Age"
        name="age"
        defaultValues={leagues?.map((league) => league.age)}
        input={<SelectInput options={["adult", "child", "teen"]} />}
        helperTextUiStringKey="readmin.league.fields.age"
      />
      <MultiValuesField
        label="Gender"
        name="gender"
        defaultValues={leagues?.map((league) => league.gender)}
        input={<SelectInput options={["female", "male", "mixed"]} />}
        helperTextUiStringKey="readmin.league.fields.gender"
      />
      <MultiValuesField
        label="Active"
        name="active"
        defaultValues={leagues?.map((league) => league.active)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.league.fields.active"
      />
      <MultiValuesField
        label="Ad Group"
        name="adPreset"
        required
        values={adPresets}
        onChange={adPresetsSet}
        input={<ReadminObjectInput optionsTypename="AdPreset" />}
        helperTextUiStringKey="readmin.league.fields.ad_preset"
      />
      <MultiValuesField
        label="Plays of the Week"
        name="potwEnabled"
        values={potwEnableds}
        onChange={potwEnabledsSet}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.league.fields.potw_enabled"
      />
      {isAdmin && (
        <MultiValuesField
          label="AI Stats"
          name="aiStatsEnabled"
          values={aiStatsEnableds}
          onChange={aiStatsEnabledsSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.ai_stats_enabled"
        />
      )}
      {(isAdmin || leagues?.some((league) => league.org?.allowEncodeHighQualityOutputs)) && (
        <FormSection header="Encoding Settings" defaultExpanded>
          <MultiValuesField
            label="Encode High Quality Outputs"
            name="encodeHighQualityOutputs"
            values={encodeHighQualityOutputs}
            onChange={encodeHighQualityOutputsSet}
            input={<SwitchInput />}
            helperTextUiStringKey="readmin.league.fields.encode_high_quality_outputs"
          />
        </FormSection>
      )}
      <FormSection header="Privacy Settings" defaultExpanded>
        <MultiValuesField
          label="Video Recording Permitted"
          name="videoRecordingPermitted"
          values={videoRecordingPermitteds}
          onChange={videoRecordingPermittedsSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.video_recording_permitted"
        />
        <MultiValuesField
          label="Disable Sharing"
          name="sharingDisabled"
          values={sharingDisableds}
          onChange={sharingDisabledsSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.sharing_disabled"
        />
        <MultiValuesField
          label="Anonymise People"
          name="anonymisePeople"
          values={anonymisePeoples}
          onChange={anonymisePeoplesSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.anonymise_people"
        />
      </FormSection>
      <FormSection header="Leaderboard Settings" defaultExpanded>
        <MultiValuesField
          label="Show Team Standing"
          name="leaderboardTeamStandingVisible"
          values={leaderboardTeamStandingVisibles}
          onChange={leaderboardTeamStandingVisiblesSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.leaderboard_team_standing_visible"
        />
        <MultiValuesField
          label="Points for Win"
          name="pointsForWin"
          values={pointsForWins}
          onChange={pointsForWinsSet}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.league.fields.points_for_win"
        />
        <MultiValuesField
          label="Points for Draw"
          name="pointsForDraw"
          values={pointsForDraws}
          onChange={pointsForDrawsSet}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.league.fields.points_for_draw"
        />
        <MultiValuesField
          label="Points for Loss"
          name="pointsForLoss"
          values={pointsForLosses}
          onChange={pointsForLossesSet}
          input={<NumberInput />}
          required
          helperTextUiStringKey="readmin.league.fields.points_for_loss"
        />
      </FormSection>
      <FormSection header="Live Scoreboard Settings" defaultExpanded>
        <MultiValuesField
          label="Show Fouls"
          name="scoreboardFoulsVisible"
          values={scoreboardFoulsVisibles}
          onChange={scoreboardFoulsVisiblesSet}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.league.fields.scoreboard_fouls_visible"
        />
      </FormSection>
    </>
  );
}
